import React, { useRef, useEffect } from 'react';
import { Phone, Mic, Video, X, Sparkles } from 'lucide-react';

const FaceTimeModal = ({
  isOpen,
  onClose,
  activeCall,
  isCalling,
  setIsCalling,
  callDuration
}) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && videoRef.current && !videoRef.current.srcObject) {
      navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        .then(stream => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch(err => {
          console.log('Camera access denied:', err);
        });
    }
    
    return () => {
      if (videoRef.current?.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [isOpen]);

  if (!isOpen || !activeCall) return null;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-black border border-gray-400 rounded-2xl w-[800px] h-[600px] relative overflow-hidden hover:border-gray-300 transition-all duration-300" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Video Area (Simulated with dark background) */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
          {/* Contact Avatar as placeholder for video */}
          <div className="flex flex-col items-center gap-4">
            {activeCall.avatar ? (
              <img
                src={activeCall.avatar}
                alt={activeCall.name}
                className="w-48 h-48 rounded-full object-cover"
              />
            ) : (
              <div className="w-48 h-48 rounded-full bg-gray-700 flex items-center justify-center">
                <Video size={64} className="text-gray-400" />
              </div>
            )}
            <div className="text-center">
              <h3 className="text-3xl font-bold text-white mb-2">{activeCall.name}</h3>
              <p className="text-green-400 font-medium text-lg">
                {isCalling ? 'Connecting...' : formatDuration(callDuration)}
              </p>
            </div>
          </div>
        </div>

        {/* Self Video Preview (Picture-in-Picture) */}
        <div className="absolute top-4 right-4 w-48 h-36 bg-gray-800 border-2 border-gray-400/30 rounded-xl overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>

        {/* Call Info Overlay */}
        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg">
          <p className="text-white font-medium">{activeCall.name}</p>
          <p className="text-sm text-gray-300">FaceTime Video</p>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-6">
          <button
            onClick={() => setIsCalling(!isCalling)}
            className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
            title="Mute"
          >
            <Mic size={24} className="text-white" />
          </button>
          <button
            onClick={() => {}}
            className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
            title="Turn off camera"
          >
            <Video size={24} className="text-white" />
          </button>
          <button
            onClick={onClose}
            className="p-5 bg-red-600 hover:bg-red-700 rounded-full transition-colors shadow-lg"
            title="End Call"
          >
            <Phone size={28} className="text-white rotate-[135deg]" />
          </button>
          <button
            onClick={() => {}}
            className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
            title="Switch camera"
          >
            <X size={24} className="text-white rotate-180" />
          </button>
          <button
            onClick={() => {}}
            className="p-4 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700 rounded-full transition-colors border border-gray-400/20"
            title="Full screen"
          >
            <Sparkles size={24} className="text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaceTimeModal;
